import { v4 } from 'uuid'

import type { ISO8601 } from '@app/constants/Misc'

import { createSelector } from '@app/utils/createSelector'
import moment from '@app/utils/moment'

import { createReduxSlice } from '@app/store/redux_slice'

type RoutingState = { isInitial: boolean; uuid: string; date: ISO8601; isTransitioning: boolean }

const routingSlice = createReduxSlice<RoutingState>('routing')

/**
 * Returns the routing state, synthesizing a fresh initial value when the slice is empty.
 * Memoization keeps that initial object stable per store, so each SSR request gets its own
 * uuid/date instead of sharing a module-level constant.
 */
export const routingSelector = createSelector([routingSlice.selector], routing => routing ?? createInitialRouting())

export const routingIsTransitioningSelector = createSelector([routingSelector], routing => routing.isTransitioning)
export const routingUUIDSelector = createSelector([routingSelector], routing => routing.uuid)
export const routingDateSelector = createSelector([routingSelector], routing => moment(routing.date))

/** A route began resolving. */
export const beginRoutingSlice = (initial: boolean) =>
  routingSlice.update(state => ({ ...(state ?? createInitialRouting()), isInitial: initial, isTransitioning: true }))

/** A route committed. The initial navigation keeps the uuid it was rendered with; every later one gets a fresh one. */
export const commitRoutingSlice = () =>
  routingSlice.update(state => {
    const current = state ?? createInitialRouting()
    return { ...current, isTransitioning: false, uuid: current.isInitial ? current.uuid : v4(), date: moment().format() }
  })

/** Stamps the routing date at the start of an SSR request, so `routingDateSelector` is that request's clock. */
export const initServerRoutingSlice = () => routingSlice.update(state => ({ ...(state ?? createInitialRouting()), date: moment().format() }))

const createInitialRouting = (): RoutingState => ({ isInitial: true, isTransitioning: false, uuid: v4(), date: moment().format() })
